import { isSessionUsable, type Session } from './auth-client';

export const SESSION_STORAGE_KEY = 'wis.session';

/** localStorage, not sessionStorage, so a refresh keeps the login — trade-off: any origin script can read it, so an XSS leaks a revocable session token, not a password. */
export class SessionStore {
  private session: Session | null = null;
  private readonly listeners = new Set<(session: Session | null) => void>();

  private static readonly JOIN_NAME_KEY = 'name';

  constructor() {
    this.session = this.restore();
  }

  get current(): Session | null {
    if (this.session !== null && !isSessionUsable(this.session)) {
      this.clear();
    }
    return this.session;
  }

  get isLoggedIn(): boolean {
    return this.current !== null;
  }

  set(session: Session): void {
    this.session = session;
    this.persist(session);
    this.notify();
  }

  clear(): void {
    this.removeStoredSession();
    if (this.session === null) return;
    this.session = null;
    this.notify();
  }

  subscribe(listener: (session: Session | null) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  rememberedJoinName(): string | null {
    try {
      return localStorage.getItem(SessionStore.JOIN_NAME_KEY);
    } catch {
      return null;
    }
  }

  rememberJoinName(name: string): void {
    try {
      localStorage.setItem(SessionStore.JOIN_NAME_KEY, name);
    } catch {}
  }

  private restore(): Session | null {
    let stored: string | null;
    try {
      stored = localStorage.getItem(SESSION_STORAGE_KEY);
    } catch {
      return null;
    }
    if (stored === null) return null;

    try {
      const session = parseStoredSession(JSON.parse(stored));
      if (session === null || !isSessionUsable(session)) {
        this.removeStoredSession();
        return null;
      }
      return session;
    } catch {
      this.removeStoredSession();
      return null;
    }
  }

  private persist(session: Session): void {
    try {
      localStorage.setItem(
        SESSION_STORAGE_KEY,
        JSON.stringify({
          token: session.token,
          tokenType: session.tokenType,
          expiresAt: session.expiresAt.toISOString(),
          accountId: session.accountId,
          legacyId: session.legacyId,
          username: session.username,
        }),
      );
    } catch {}
  }

  private removeStoredSession(): void {
    try {
      localStorage.removeItem(SESSION_STORAGE_KEY);
    } catch {}
  }

  private notify(): void {
    for (const listener of this.listeners) listener(this.session);
  }
}

function parseStoredSession(raw: unknown): Session | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const value = raw as Record<string, unknown>;

  if (typeof value.token !== 'string' || value.token === '') return null;
  if (typeof value.tokenType !== 'string' || value.tokenType === '') return null;
  if (typeof value.expiresAt !== 'string') return null;
  const expiresAt = new Date(value.expiresAt);
  if (!Number.isFinite(expiresAt.getTime())) return null;
  if (typeof value.accountId !== 'number' || !Number.isFinite(value.accountId)) return null;
  if (typeof value.username !== 'string') return null;
  if (value.legacyId !== null && typeof value.legacyId !== 'number') return null;

  return {
    token: value.token,
    tokenType: value.tokenType,
    expiresAt,
    accountId: value.accountId,
    legacyId: value.legacyId,
    username: value.username,
  };
}
